import { faEnvelope, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@pickleballinc/react-ui';
import clsx from 'clsx';

import { useSession } from '@/lib/SessionProvider';
import { getNeedAPartnerURL } from '@/tournament/components/players/PlayerRow';
import { getConnectDUPRURL } from '@/utils/helpers/getConnectDUPRURL';
import { isCurrentUser } from '@/utils/helpers/helpers';

export interface EventPlayer {
	playerFullName: string;
	partnerFullName: string;
	playerSkill: string;
	partnerSkill: string;
	playerAge: number;
	partnerAge: number;
	playerCityState: string;
	partnerCityState: string;
	needAPartner: boolean;
	playerId: string;
	partnerId?: string;
	playerSlug: string;
	partnerSlug?: string;
	isPartnerEvent: boolean;
	isOnWaitlist: boolean;
	isLottery: boolean;
	isRegistered: boolean;
	attendeeActivityId: string;
	playerGender: string;
	partnerGender: string;
	isPartnerRegistered: boolean;
	playerDuprActive: boolean;
	partnerDuprActive: boolean;
}

interface EventPlayerProps {
	player: EventPlayer;
	isProLevelBracket: boolean;
}

export const EventPlayerRow = ({ player, isProLevelBracket }: EventPlayerProps) => {
	const { session } = useSession();

	return (
		<>
			<tr className={clsx('bg-white text-sm', !player.isPartnerEvent && 'border-b-2 border-gray-100')}>
				<td className={clsx('relative w-[280px] min-w-[280px] px-4', player.isPartnerEvent ? 'py-1' : 'py-2')}>
					<div
						className={clsx(
							player.isPartnerEvent
								? 'before:absolute before:bottom-0 before:left-0 before:top-[8px] before:w-1 before:bg-blue-100'
								: '-ml-4 border-l-4 border-blue-100 pl-3 '
						)}
					>
						{player.isOnWaitlist && (
							<span className="mr-1 inline-block w-4 rounded-full bg-[#EAAA08] text-center text-xs font-normal text-white">W</span>
						)}

						{player.isLottery && (
							<span className="mr-1 inline-block w-4 rounded-full bg-blue-700 text-center text-xs font-normal text-white">L</span>
						)}
						{player.playerId ? (
							<>
								<a
									href={`https://${process.env.NEXT_PUBLIC_FRONT_URL}/players/${player.playerSlug || player.playerId}`}
									className="text-blue-600 hover:underline"
								>
									{player.playerFullName}
								</a>

								{!player.playerDuprActive && (
									<>
										{isCurrentUser(session, player.playerId) ? (
											<div>
												<a
													className="text-sm text-blue-600 underline"
													href={getConnectDUPRURL(player.playerId, 't', window.location.href, window.location.href)}
												>
													Connect To DUPR
												</a>
											</div>
										) : (
											<div className="text-xs text-gray-500">(Not Connected to DUPR)</div>
										)}
									</>
								)}
							</>
						) : (
							<div className="pt-2">{player.playerFullName}</div>
						)}
					</div>
				</td>

				{!isProLevelBracket && (
					<td className="w-[100px] min-w-[100px] px-4 py-1 text-center">
						<div>
							<div>{player.playerSkill || '--'}</div>
						</div>
					</td>
				)}

				<td className="w-[100px] min-w-[100px] px-4 py-1 text-center">
					<div>
						<div>{player.playerAge > 0 ? player.playerAge : '--'}</div>
					</div>
				</td>
				<td className="w-[100px] min-w-[100px] px-4 py-1 text-center">
					<div>
						<div>{player.playerGender || '--'}</div>
					</div>
				</td>
				<td className="w-[230px] min-w-[230px] px-4 py-1">
					<div>
						<div>{player.playerCityState || '--'}</div>
					</div>
				</td>
			</tr>
			{player.isPartnerEvent && (
				<tr className="border-b-2 border-gray-100 bg-white text-sm">
					<td className="relative w-[280px] min-w-[280px] px-4 py-1">
						<div className="before:absolute before:bottom-[8px] before:left-0 before:top-0 before:w-1 before:bg-blue-100">
							{player.needAPartner ? (
								<a
									className="block w-max text-xs text-blue-500 hover:underline"
									target="_blank"
									href={getNeedAPartnerURL(player.attendeeActivityId)}
									aria-label="Link to send partner request (opens in a new tab)"
								>
									<FontAwesomeIcon icon={faEnvelope} /> Send partner request
								</a>
							) : (
								<>
									{player.isOnWaitlist && (
										<span className="mr-1 inline-block w-4 rounded-full bg-[#EAAA08] text-center text-xs font-normal text-white">
											W
										</span>
									)}

									{player.isLottery && (
										<span className="mr-1 inline-block w-4 rounded-full bg-blue-700 text-center text-xs font-normal text-white">
											L
										</span>
									)}
									{player.partnerId ? (
										<>
											{!player.isPartnerRegistered && (
												<Tooltip placement="bottom">
													<Tooltip.Trigger asChild>
														<FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-error-600" />
													</Tooltip.Trigger>
													<Tooltip.Content className="rounded bg-black/75 p-2 text-sm text-white">
														Not registered
													</Tooltip.Content>
												</Tooltip>
											)}

											<a
												href={`https://${process.env.NEXT_PUBLIC_FRONT_URL}/players/${player.partnerSlug || player.partnerId}`}
												className="text-blue-600 hover:underline"
											>
												{player.partnerFullName}
											</a>
											{!player.partnerDuprActive && (
												<>
													{isCurrentUser(session, player.partnerId) ? (
														<div>
															<a
																className="text-sm text-blue-600 underline"
																href={getConnectDUPRURL(
																	player.partnerId,
																	't',
																	window.location.href,
																	window.location.href
																)}
															>
																Connect To DUPR
															</a>
														</div>
													) : (
														<div className="text-xs text-gray-500">(Not Connected to DUPR)</div>
													)}
												</>
											)}
										</>
									) : (
										<div>{player.partnerFullName}</div>
									)}
								</>
							)}
						</div>
					</td>

					{!isProLevelBracket && (
						<td className="w-[100px] min-w-[100px] px-4 py-1 text-center">
							<div>
								<div>{player.needAPartner ? '--' : player.partnerSkill || '--'}</div>
							</div>
						</td>
					)}

					<td className="w-[100px] min-w-[100px] px-4 py-1 text-center">
						<div>
							<div>{player.needAPartner || !player.partnerAge ? '--' : player.partnerAge}</div>
						</div>
					</td>
					<td className="w-[100px] min-w-[100px] px-4 py-1 text-center">
						<div>
							<div>{player.needAPartner || !player.partnerGender ? '--' : player.partnerGender}</div>
						</div>
					</td>
					<td className="w-[230px] min-w-[230px] px-4 py-1">
						<div>
							<div>{player.needAPartner ? '--' : player.partnerCityState || '--'}</div>
						</div>
					</td>
					<td className="px-4 py-1">
						{player.partnerId && !player.isPartnerRegistered && (
							<div className="w-max rounded-md border border-error-600 bg-error-100 px-2 text-sm text-error-600">
								<span>&bull; </span>
								Not Registered
							</div>
						)}
					</td>
				</tr>
			)}
		</>
	);
};
